import { useState } from "react";
import { MoreVertical, ExternalLink, Archive, ArchiveRestore, Trash2, Loader2, FlaskConical } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export function RowMenu({ row, index, onOpen, onArchive, onRestore, onDelete, onToggleTest }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" data-testid={`row-menu-${index}`} aria-label={`Actions for ${row.horse.horse_name || "application"}`}
          onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}
          className="h-9 w-9 rounded-full flex items-center justify-center hover:bg-stone-200 text-stone-500">
          <MoreVertical className="h-4 w-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
        <DropdownMenuItem data-testid={`row-open-${index}`} onSelect={onOpen}><ExternalLink className="h-4 w-4 mr-2" />Open Application</DropdownMenuItem>
        {row.archived_at ? (
          <DropdownMenuItem data-testid={`row-restore-${index}`} onSelect={onRestore}><ArchiveRestore className="h-4 w-4 mr-2" />Restore Application</DropdownMenuItem>
        ) : (
          <DropdownMenuItem data-testid={`row-archive-${index}`} onSelect={onArchive}><Archive className="h-4 w-4 mr-2" />Archive Application</DropdownMenuItem>
        )}
        <DropdownMenuItem data-testid={`row-toggle-test-${index}`} onSelect={onToggleTest}><FlaskConical className="h-4 w-4 mr-2" />{row.is_test ? "Remove TEST mark" : "Mark as Test Application"}</DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem data-testid={`row-delete-${index}`} onSelect={onDelete} className="text-red-600 focus:text-red-700">
          <Trash2 className="h-4 w-4 mr-2" />{row.is_test ? "Delete Test Application" : "Permanently Delete Application"}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function DeleteDialog({ rows, onCancel, onConfirm }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  if (!rows) return null;
  const go = async () => {
    setBusy(true);
    try { await onConfirm(); } finally { setBusy(false); setText(""); }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) { setText(""); onCancel(); } }}>
      <DialogContent data-testid="delete-dialog" className="w-[calc(100%-1.5rem)] rounded-xl">
        <DialogHeader>
          <DialogTitle>Permanently delete {rows.length === 1 ? "application" : `${rows.length} applications`}?</DialogTitle>
          <DialogDescription>
            This removes the application, its messages, photos, queued notifications and any TEST agreements (including signed TEST PDFs, signatures and certificates). It cannot be undone. Seller accounts, horse records and real signed agreements are never deleted.
          </DialogDescription>
        </DialogHeader>
        <ul data-testid="delete-list" className="max-h-40 overflow-y-auto text-sm border border-stone-200 rounded-lg divide-y divide-stone-100">
          {rows.map((r) => (
            <li key={r.id} className="px-3 py-2">{r.is_test && <span className="mr-1 rounded bg-fuchsia-600 text-white text-[10px] font-extrabold px-1.5 py-0.5">TEST</span>}<strong>{r.horse.horse_name || "Unnamed horse"}</strong> <span className="text-stone-500">— {r.contact.full_name || "Unknown seller"}</span></li>
          ))}
        </ul>
        <label htmlFor="delete-confirm" className="text-sm font-semibold text-stone-800">Type <span className="font-mono text-red-700">DELETE</span> to confirm</label>
        <Input id="delete-confirm" data-testid="delete-confirm-input" value={text} onChange={(e) => setText(e.target.value)} autoComplete="off" className="h-11" />
        <DialogFooter className="gap-2">
          <Button data-testid="delete-cancel" variant="outline" onClick={() => { setText(""); onCancel(); }} className="h-11 rounded-full">Cancel</Button>
          <Button data-testid="delete-confirm" disabled={text !== "DELETE" || busy} onClick={go} className="h-11 rounded-full bg-red-600 hover:bg-red-700 text-white">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Trash2 className="h-4 w-4 mr-1.5" />Delete permanently</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
